import { Completion, CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { Diagnostic } from '@codemirror/lint'
import { StateEffect, StateField } from '@codemirror/state'
import {
  Decoration,
  DecorationSet,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  ViewUpdate,
} from '@codemirror/view'
import {
  AverageFunctionContext,
  AverageIfFunctionContext,
  CeilFunctionContext,
  ConcatFunctionContext,
  CountDistinctFunctionContext,
  CountFunctionContext,
  CountIfFunctionContext,
  DateaddFunctionContext,
  DatediffFunctionContext,
  DateFunctionContext,
  DayFunctionContext,
  ErrorToken,
  FloorFunctionContext,
  FormulaDisplayColumn,
  formulaLexer,
  getTableColumnDisplay,
  IfFunctionTypeContext,
  LeftFunctionContext,
  LenFunctionContext,
  LogicExprContext,
  LogicTermContext,
  LowerFunctionContext,
  MaxFunctionContext,
  MaxIfsFunctionContext,
  MidFunctionContext,
  MinFunctionContext,
  MinIfsFunctionContext,
  MonthFunctionContext,
  RightFunctionContext,
  RoundFunctionContext,
  SubstituteFunctionContext,
  SumExpressionContext,
  SumFunctionContext,
  SumIfFunctionContext,
  SumProductFunctionContext,
  ToDateFunctionContext,
  ToNumberFunctionContext,
  ToTextFunctionContext,
  TrimFunctionContext,
  TwoExpressionsContext,
  UpperFunctionContext,
  YearFunctionContext,
} from '@doss/common-all'
import { CommonToken, ParserRuleContext } from 'antlr4'
import { isEmpty, isNil } from 'lodash'
import { DataType } from '~/common/generated/apollo-operations'
import { ColumnChipWidget } from './ColumnChipWidget'
import { ErrorIconWidget } from './ErrorIconWidget'
import { FormulaFieldReference } from './types'

/**
 * Function parse contexts grouped by the set of operand columns the autocomplete offers inside the
 * call. TO_NUMBER takes a string (or a number) operand, so it sits in the text group whose types
 * cover both. TO_TEXT and TO_DATE stay in the text group only for routing/relevance but get their
 * own operand branches in `getCompletionsForFunctionType` (TO_TEXT: every scalar; TO_DATE:
 * text-likes plus date).
 */
const NUMERIC_FUNCTION_CONTEXTS = [
  SumFunctionContext,
  CountFunctionContext,
  CountDistinctFunctionContext,
  AverageFunctionContext,
  SumProductFunctionContext,
  MaxFunctionContext,
  MinFunctionContext,
  CeilFunctionContext,
  FloorFunctionContext,
  RoundFunctionContext,
] as const

const TEXT_FUNCTION_CONTEXTS = [
  ConcatFunctionContext,
  UpperFunctionContext,
  LowerFunctionContext,
  LeftFunctionContext,
  RightFunctionContext,
  MidFunctionContext,
  LenFunctionContext,
  TrimFunctionContext,
  SubstituteFunctionContext,
  // TO_NUMBER parses a string literal/column (or passes a number through), covered by this group.
  ToNumberFunctionContext,
  // TO_DATE and TO_TEXT are bucketed here only so they route as relevant function contexts; their
  // operands are handled by dedicated branches in getCompletionsForFunctionType.
  ToDateFunctionContext,
  ToTextFunctionContext,
] as const

const DATE_FUNCTION_CONTEXTS = [
  DateFunctionContext,
  DateaddFunctionContext,
  DatediffFunctionContext,
  DayFunctionContext,
  MonthFunctionContext,
  YearFunctionContext,
] as const

type NumericFunctionContext = InstanceType<(typeof NUMERIC_FUNCTION_CONTEXTS)[number]>
type TextFunctionContext = InstanceType<(typeof TEXT_FUNCTION_CONTEXTS)[number]>
type DateFunctionsContext = InstanceType<(typeof DATE_FUNCTION_CONTEXTS)[number]>

/** Any function-call context whose completions are resolved by `getCompletionsForFunctionType`. */
type FunctionCallTypeContext = NumericFunctionContext | TextFunctionContext | DateFunctionsContext

export const isNumericFunctionContext = (ctx: ParserRuleContext): ctx is NumericFunctionContext =>
  NUMERIC_FUNCTION_CONTEXTS.some((Ctx) => ctx instanceof Ctx)

export const isTextFunctionContext = (ctx: ParserRuleContext): ctx is TextFunctionContext =>
  TEXT_FUNCTION_CONTEXTS.some((Ctx) => ctx instanceof Ctx)

export const isDateFunctionContext = (ctx: ParserRuleContext): ctx is DateFunctionsContext =>
  DATE_FUNCTION_CONTEXTS.some((Ctx) => ctx instanceof Ctx)

/**
 * Creates a CodeMirror Completion for a Function Type
 */
const createFunctionCompletion = (label: string): Completion => {
  return {
    label,
    type: 'function',
    apply: `${label}(`,
  }
}

const FUNCTION_TYPE_COMPLETIONS: Completion[] = [
  // Numeric functions
  createFunctionCompletion('MAX'),
  createFunctionCompletion('MIN'),
  createFunctionCompletion('SUM'),
  createFunctionCompletion('COUNT'),
  createFunctionCompletion('COUNT_DISTINCT'),
  createFunctionCompletion('AVERAGE'),
  createFunctionCompletion('CEIL'),
  createFunctionCompletion('FLOOR'),
  createFunctionCompletion('ROUND'),
  createFunctionCompletion('SUMIF'),
  createFunctionCompletion('COUNTIF'),
  createFunctionCompletion('AVERAGEIF'),
  createFunctionCompletion('MINIFS'),
  createFunctionCompletion('MAXIFS'),
  createFunctionCompletion('IFS'),
  // Text functions
  createFunctionCompletion('UPPER'),
  createFunctionCompletion('LOWER'),
  createFunctionCompletion('TRIM'),
  createFunctionCompletion('LEN'),
  createFunctionCompletion('CONCAT'),
  createFunctionCompletion('LEFT'),
  createFunctionCompletion('RIGHT'),
  createFunctionCompletion('MID'),
  createFunctionCompletion('SUBSTITUTE'),
  // Date functions
  createFunctionCompletion('DATE'),
  createFunctionCompletion('DATEADD'),
  createFunctionCompletion('DATEDIFF'),
  createFunctionCompletion('YEAR'),
  createFunctionCompletion('MONTH'),
  createFunctionCompletion('DAY'),
  // Type conversion functions
  createFunctionCompletion('TO_TEXT'),
  createFunctionCompletion('TO_NUMBER'),
  createFunctionCompletion('TO_DATE'),
  {
    label: '(',
    type: 'function',
  },
]

/**
 * TODAY/NOW are bare keyword literals (no parentheses), so they insert their own text rather than a
 * `NAME(` call. (forms only)
 */
const DATE_TIME_TOKEN_COMPLETIONS: Completion[] = [
  { label: 'TODAY', type: 'constant', apply: 'TODAY' },
  { label: 'NOW', type: 'constant', apply: 'NOW' },
]

/** Aggregations over more than one source column, offered to table formulas only. */
const MULTI_COLUMN_AGGREGATION_COMPLETIONS: Completion[] = [createFunctionCompletion('SUMPRODUCT')]

const LOGIC_OPERATOR_COMPLETIONS: Completion[] = [
  { label: '>' },
  { label: '>=' },
  { label: '<' },
  { label: '<=' },
  { label: '=' },
  { label: '!-' },
  { label: 'CONTAINS' },
  { label: 'CONTAINS ANY' },
  { label: 'CONTAINS ALL' },
  { label: 'DOES NOT CONTAIN' },
  { label: 'DOES NOT CONTAIN ANY' },
  { label: 'IN' },
  { label: 'NOT IN' },
  { label: 'IS EMPTY' },
  { label: 'IS NOT EMPTY' },
]

/**
 * Defines a StateEffect to add a visual mark to a specific range of text in the editor.
 */
export const addTokenMark = StateEffect.define<{ from: number; to: number; mark: Decoration }>({
  map: ({ from, to, mark }, change) => ({
    from: change.mapPos(from), // Adjusts the start position for changes in the document
    to: change.mapPos(to), // Adjusts the end position for changes in the document
    mark, // The decoration to apply
  }),
})

/**
 * Defines a StateField to manage a set of decorations (visual marks) within the editor.
 * It tracks the current state of decorations and updates them based on document changes
 * or new marks.
 */
export const markField = StateField.define<DecorationSet>({
  create() {
    // Initially no decorations are present
    return Decoration.none
  },
  update(decorations, transaction) {
    // Update decorations for any document changes
    decorations = decorations.map(transaction.changes)

    // Iterate over effects in the transaction
    transaction.effects.forEach((effect) => {
      if (effect.is(addTokenMark)) {
        // Add token decoration
        const { from, to, mark } = effect.value
        decorations = decorations.update({
          add: [mark.range(from, to)],
        })
      }
    })

    // Return the updated decorations
    return decorations
  },
  provide: (f) => EditorView.decorations.from(f), // Provides these decorations to the editor view
})

/**
 * Defines a StateEffect to update the list of error tokens (segments of text with errors).
 * This effect is used to dynamically adjust the set of errors as edits are made or new errors
 * are detected.
 */
export const setErrors = StateEffect.define<ErrorToken[]>()

/**
 * Defines a StateField to track the current list of error tokens within the editor.
 * This field is essential for maintaining and displaying error states across editor transactions.
 */
export const errorStateField = StateField.define<{
  errors: ErrorToken[]
  isEmpty: boolean
  isInteracted: boolean
}>({
  create() {
    return { errors: [], isEmpty: false, isInteracted: false } // Initially, no errors
  },

  update(errorState, transaction) {
    // Set error tokens with the existing errors
    let errorTokens = errorState.errors
    let isInteracted = errorState.isInteracted

    // Set interaction flag on typing or focus/blur events
    if (
      transaction.docChanged ||
      transaction.isUserEvent('focus') ||
      transaction.isUserEvent('blur')
    ) {
      isInteracted = true
    }
    // Apply effects that modify the error list
    transaction.effects.forEach((effect) => {
      if (effect.is(setErrors)) {
        // Update the error tokens if the transaction effect is for setting errors
        errorTokens = effect.value
      }
    })

    // Check if the input is empty
    const text = transaction.state.doc.toString()
    const isEmpty = text.trim() === ''

    return { errors: errorTokens, isEmpty, isInteracted }
  },
})

/**
 * Computes editor attributes to dynamically add a CSS class based on the error state.
 * This allows the editor to visually indicate an error state by changing the border color.
 */
export const errorClassExtension = EditorView.editorAttributes.from(
  errorStateField,
  (errorState) => {
    // Only apply the error class if the editor has been initialized
    if (!errorState.isInteracted) {
      return { class: '' }
    }

    return { class: errorState.errors.length > 0 || errorState.isEmpty ? 'error' : '' }
  },
)

/**
 * Function to create diagnostics for display in the editor based on current errors.
 * Diagnostics are used by the CodeMirror linter to show error messages and indications in the UI.
 */
export function lint(view: EditorView): Diagnostic[] {
  const errorState = view.state.field(errorStateField)

  return errorState.errors.map((error) => ({
    from: error.start,
    to: error.end,
    severity: 'error',
    message: error.message,
    source: error.isBuilderError ? 'formula' : 'grammar',
    markClass: 'cm-error',
  }))
}

/**
 * Returns the code mirror view plugin for displaying the error icon within the text input
 */
export function errorWidgetPlugin(
  errorStateField: StateField<{ errors: ErrorToken[]; isEmpty: boolean; isInteracted: boolean }>,
): ViewPlugin<{ decorations: DecorationSet }> {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet

      constructor() {
        this.decorations = Decoration.none
      }

      update(update: ViewUpdate) {
        const errorState = update.state.field(errorStateField)
        const hasErrors = errorState.errors.length > 0 || errorState.isEmpty
        const isInteracted = errorState.isInteracted
        if (hasErrors && isInteracted) {
          const widget = new ErrorIconWidget()
          const decoration = Decoration.widget({
            widget,
            side: 1, // Places the widget at the end of the text (right side)
          })
          // Add the error icon if there is an error
          this.decorations = Decoration.set([decoration.range(update.state.doc.length)])
        } else {
          // Remove the error icon if there is no error
          this.decorations = Decoration.none
        }
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  )
}

/**
 * Helper function to convert FormulaFieldReference to FormulaDisplayColumn for display.
 * This provides just the fields needed by getTableColumnDisplay and ColumnChipWidget.
 */
function fieldReferenceToDisplayColumn(field: FormulaFieldReference): FormulaDisplayColumn {
  return {
    id: field.id,
    name: field.name,
    table: {
      id: field.contextId,
      name: field.contextName || '',
      parentTable: null, // Form inputs don't have parent tables
    },
  }
}

/**
 * Creates a match decorator for table column uuids in the code mirror editor to replace the
 * uuids with the formula column chip
 */
function createColumnDecorator(
  fields: FormulaFieldReference[],
  currentContextId: string | undefined,
  errors: ErrorToken[],
): MatchDecorator {
  return new MatchDecorator({
    regexp: /{[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}}/g,
    decoration: (match, _, pos) => {
      const columnId = match[0].slice(1, -1)
      const field = fields.find((f) => f.id === columnId)
      if (field) {
        const hasError = errors.some((error) => {
          const errorColumnId = error.offendingSymbol.text.substring(
            1,
            error.offendingSymbol.text.length - 1,
          )
          return (
            errorColumnId === columnId &&
            error.offendingSymbol.start <= pos &&
            error.offendingSymbol.stop >= pos
          )
        })
        // Convert to FormulaDisplayColumn for widget compatibility
        const displayColumn = fieldReferenceToDisplayColumn(field)
        return Decoration.replace({
          widget: new ColumnChipWidget(displayColumn, currentContextId, hasError),
          class: `column-chip${hasError ? '-error' : ''}`,
        })
      }
      return null
    },
  })
}

/**
 * Returns the code mirror view plugin for replacing the column uuids with the column chip
 */
export const columnDecorationPlugin = (
  fields: FormulaFieldReference[],
  currentContextId: string | undefined,
  errorStateField: StateField<{ errors: ErrorToken[]; isEmpty: boolean }>,
): ViewPlugin<{ decorations: DecorationSet; columnDecorator: MatchDecorator }> =>
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet
      columnDecorator: MatchDecorator

      constructor(view: EditorView) {
        this.columnDecorator = createColumnDecorator(
          fields,
          currentContextId,
          view.state.field(errorStateField).errors,
        )
        this.decorations = this.columnDecorator.createDeco(view)
      }

      update(update: ViewUpdate) {
        this.columnDecorator = createColumnDecorator(
          fields,
          currentContextId,
          update.state.field(errorStateField).errors,
        )
        this.decorations = this.columnDecorator.createDeco(update.view)
      }
    },
    {
      decorations: (instance) => instance.decorations,
      provide: (plugin) =>
        EditorView.atomicRanges.of((view) => {
          return view.plugin(plugin)?.decorations || Decoration.none
        }),
    },
  )

/**
 * Returns the autocomplete items based on the current position of the cursor
 */
function getAutocompleteItems(
  context: CompletionContext,
  fields: FormulaFieldReference[],
  autocompleteContext: ParserRuleContext | null,
  currentContextId: string | undefined,
  parentContextId?: string | null,
  languageScope: 'formula' | 'logicalExpression' = 'formula',
  allowDateTimeTokens = false,
  allowMultiColumnAggregations = false,
): CompletionResult | null {
  // Match the word before the cursor
  const wordBeforeCursor = context.matchBefore(/\w*/)

  // If there's no word before the cursor and it's not an explicit completion request, return null
  if (!wordBeforeCursor || (wordBeforeCursor.from === wordBeforeCursor.to && !context.explicit)) {
    return null
  }

  const options = getAutocompleteItemsForContext(
    autocompleteContext,
    fields,
    wordBeforeCursor.from,
    currentContextId,
    parentContextId,
    languageScope,
    allowDateTimeTokens,
    allowMultiColumnAggregations,
  )

  // Filter the options on the user's input
  const filteredOptions = options.filter(
    (option) =>
      option.label.toLowerCase().includes(wordBeforeCursor.text.toLowerCase()) ||
      option.displayLabel?.toLowerCase().includes(wordBeforeCursor.text.toLowerCase()),
  )

  // Return a CompletionResult with options containing the autocomplete items
  return {
    from: wordBeforeCursor.from,
    options: filteredOptions,
    validFor: () => true,
  }
}

/**
 * The state effect for triggering the autocomplete display
 */
export const triggerAutocompleteEffect = StateEffect.define<{
  fields: FormulaFieldReference[]
  currentContextId: string | undefined
  parentContextId?: string | null
  autocompleteContext: ParserRuleContext | null
  languageScope: 'formula' | 'logicalExpression'
  allowDateTimeTokens?: boolean
  allowMultiColumnAggregations?: boolean
}>()

/**
 * The state field for managing where the autocomplete display should appear
 */
export const autocompleteField = StateField.define<CompletionResult | null>({
  create() {
    return null
  },
  update(value, transaction) {
    for (const effect of transaction.effects) {
      if (effect.is(triggerAutocompleteEffect)) {
        const {
          fields,
          currentContextId,
          autocompleteContext,
          parentContextId,
          languageScope,
          allowDateTimeTokens,
          allowMultiColumnAggregations,
        } = effect.value
        // Recalculate the autocomplete suggestions
        const context = new CompletionContext(
          transaction.state,
          transaction.state.selection.main.to,
          false,
        )
        return getAutocompleteItems(
          context,
          fields,
          autocompleteContext,
          currentContextId,
          parentContextId,
          languageScope,
          allowDateTimeTokens,
          allowMultiColumnAggregations,
        )
      }
    }
    return value
  },
})

/**
 * Returns the Completion for a field (table column or form input)
 */
const getFieldAutocompletionOption = (
  field: FormulaFieldReference,
  start: number,
  currentContextId: string | undefined,
): Completion => {
  // Convert to FormulaDisplayColumn for display compatibility
  const displayColumn = fieldReferenceToDisplayColumn(field)
  return {
    label: getTableColumnDisplay(displayColumn, currentContextId),
    apply: (view: EditorView): void => {
      const { state } = view
      const { to } = state.selection.main
      view.dispatch({
        changes: {
          from: start,
          to,
          insert: `{${field.id}}`, // Insert the field ID instead of the field name
        },
      })
    },
    type: 'keyword',
  }
}

/**
 * Returns the possible autocompletion options based on the context of the cursor location.
 */
const getAutocompleteItemsForContext = (
  parserState: ParserRuleContext | null,
  fields: FormulaFieldReference[],
  start: number,
  currentContextId: string | undefined,
  parentContextId?: string | null,
  languageScope: 'formula' | 'logicalExpression' = 'formula',
  allowDateTimeTokens = false,
  allowMultiColumnAggregations = false,
): Completion[] => {
  if (!parserState) {
    return getDefaultCompletions(
      fields,
      start,
      currentContextId,
      parentContextId,
      languageScope,
      allowDateTimeTokens,
      allowMultiColumnAggregations,
    )
  }

  return getCompletionsForParserRuleContext(
    parserState,
    fields,
    start,
    currentContextId,
    parentContextId,
    languageScope,
    allowDateTimeTokens,
    allowMultiColumnAggregations,
  )
}

/**
 * Returns autocomplete options that include all the function types and the numeric table
 * columns on the formula table
 */
function getDefaultCompletions(
  fields: FormulaFieldReference[],
  start: number,
  currentContextId: string | undefined,
  parentContextId?: string | null,
  languageScope: 'formula' | 'logicalExpression' = 'formula',
  allowDateTimeTokens = false,
  allowMultiColumnAggregations = false,
): Completion[] {
  const includedContextIds = []
  if (currentContextId) {
    includedContextIds.push(currentContextId)
  }
  if (!isNil(parentContextId)) {
    includedContextIds.push(parentContextId)
  }

  // For logical expressions, allow all field types. For formulas, allow numeric, string, and date types.
  const filterOptions: FilterOptions =
    languageScope === 'logicalExpression'
      ? {
          includedContextIds: !isEmpty(includedContextIds) ? includedContextIds : undefined,
        }
      : {
          allowedTypes: [
            DataType.NUMBER,
            DataType.FORMULA_NUMBER,
            DataType.STRING,
            DataType.FORMULA_STRING,
            DataType.ENUM,
            DataType.FILE_URL,
            DataType.USER,
            DataType.ID,
            DataType.DATE,
            DataType.FORMULA_DATE,
            DataType.DATE_TIME,
            DataType.FORMULA_DATE_TIME,
            DataType.FORMULA,
          ],
          includedContextIds: !isEmpty(includedContextIds) ? includedContextIds : undefined,
        }

  const validFields = filterFields(fields, filterOptions)
  return [
    ...FUNCTION_TYPE_COMPLETIONS,
    // TODAY/NOW are only surfaced when the editor opts in (forms); tables never see them.
    ...(allowDateTimeTokens && languageScope === 'formula' ? DATE_TIME_TOKEN_COMPLETIONS : []),
    // SUMPRODUCT is the mirror image: tables opt in, forms never see it.
    ...(allowMultiColumnAggregations && languageScope === 'formula'
      ? MULTI_COLUMN_AGGREGATION_COMPLETIONS
      : []),
    ...validFields.map((field) => getFieldAutocompletionOption(field, start, currentContextId)),
  ]
}

/**
 * Returns the completions that are appropriate for the cursor's current parser rule context
 */
function getCompletionsForParserRuleContext(
  context: ParserRuleContext,
  fields: FormulaFieldReference[],
  start: number,
  currentContextId: string | undefined,
  parentContextId?: string | null,
  languageScope: 'formula' | 'logicalExpression' = 'formula',
  allowDateTimeTokens = false,
  allowMultiColumnAggregations = false,
): Completion[] {
  // If in a function type context, return the appropriate options for the specific function type.
  // The numeric/text/date guards together cover every function alternative (single-, multiple-,
  // two- and three-arg), so their type-aware operand filtering runs instead of falling through to
  // the default all-types completions.
  if (
    isNumericFunctionContext(context) ||
    isTextFunctionContext(context) ||
    isDateFunctionContext(context)
  ) {
    return getCompletionsForFunctionType(context, fields, start, currentContextId)
  }

  // If in two expressions context, return the appropriate options for the specific context
  // within the two expressions context
  if (context instanceof TwoExpressionsContext && context.children) {
    return getCompletionsForTwoExpressionsContext(context, fields, start, currentContextId)
  }

  // If in the if function type context, return options depending on whether the operator is
  // SUMIF, COUNTIF, MAXIFS, MINIFS and which half of the context the user's cursor exists
  if (context instanceof IfFunctionTypeContext) {
    return getCompletionsForIfFunctionType(context, fields, start, currentContextId)
  }

  // If within a logic term context, return all available logic operators and all columns
  if (context instanceof LogicTermContext) {
    return [
      ...LOGIC_OPERATOR_COMPLETIONS,
      ...fields.map((field) => getFieldAutocompletionOption(field, start, currentContextId)),
    ]
  }

  // Fallback to returning the function types and numerical columns on current table or parent table
  return getDefaultCompletions(
    fields,
    start,
    currentContextId,
    parentContextId,
    languageScope,
    allowDateTimeTokens,
    allowMultiColumnAggregations,
  )
}

/**
 * Determines the closest context to the given position within multiple contexts.
 */
function getClosestContext(
  position: number,
  ...contexts: (ParserRuleContext | undefined)[]
): ParserRuleContext | undefined {
  let closestContext: ParserRuleContext | undefined
  let minDistance = Infinity

  contexts.forEach((context) => {
    if (context) {
      const distance = calculateDistance(position, context)
      if (distance < minDistance) {
        minDistance = distance
        closestContext = context
      }
    }
  })

  return closestContext
}

/**
 * Helper function to get the closest context between logic and formula expressions.
 */
function getClosestExpressionContext(
  context: TwoExpressionsContext,
  start: number,
): ParserRuleContext | undefined {
  // First check if the cursor is at the logic expression half or the formula expression half
  const logicExprContext = context.children?.find(
    (child): child is LogicExprContext => child instanceof LogicExprContext,
  )
  const formulaExprContext = context.children?.find(
    (child): child is SumExpressionContext => child instanceof SumExpressionContext,
  )

  // Determine which context is closest to the given position
  return getClosestContext(start, logicExprContext, formulaExprContext)
}

/**
 * Calculates the distance between a position and a context.
 */
export function calculateDistance(position: number, ctx: ParserRuleContext): number {
  const start = ctx.start.start
  const stop = ctx.stop ? ctx.stop.stop : ctx.start.stop

  if (position < start) {
    return start - position
  } else if (position > stop) {
    return position - stop
  } else {
    return 0
  }
}

interface FilterOptions {
  allowedTypes?: DataType[]
  includedContextIds?: string[]
  excludedContextIds?: string[]
}

/**
 * Filters fields based on the given criteria.
 */
function filterFields(
  fields: FormulaFieldReference[],
  filterOptions: FilterOptions,
): FormulaFieldReference[] {
  const { allowedTypes, includedContextIds, excludedContextIds } = filterOptions

  return fields.filter((field) => {
    // Check if the field's context ID is in the included context IDs (if provided)
    const isInIncludedContext = !includedContextIds || includedContextIds.includes(field.contextId)

    // Check if the field's context ID is not in the excluded context IDs (if provided)
    const isNotInExcludedContext =
      !excludedContextIds || !excludedContextIds.includes(field.contextId)

    // Check if the field type or lookup column type is in the allowed types (if provided)
    const isOfType =
      !allowedTypes ||
      allowedTypes.includes(field.type) ||
      (field.type === DataType.LOOKUP &&
        field.lookupColumn &&
        allowedTypes.includes(field.lookupColumn.type))

    // The field is included if it meets all the specified criteria
    return isInIncludedContext && isNotInExcludedContext && isOfType
  })
}

/**
 * Returns the autocomplete options that are approriate for the two expressions context
 */
function getCompletionsForTwoExpressionsContext(
  context: TwoExpressionsContext,
  fields: FormulaFieldReference[],
  start: number,
  currentContextId: string | undefined,
): Completion[] {
  // Determine which context is closest to the given position
  const closestContext = getClosestExpressionContext(context, start)

  if (closestContext) {
    if (closestContext instanceof LogicExprContext) {
      // Return all fields for logic expression context
      return fields.map((field) => getFieldAutocompletionOption(field, start, currentContextId))
    }
    if (closestContext instanceof SumExpressionContext) {
      // Check if this Two Expression Context is for a SUMIF, COUNTIF, MAXIFS, or MINIFS by
      // checking the first child of the parent
      const firstSibling = context.parentCtx?.getChild(0)
      if (firstSibling && firstSibling instanceof IfFunctionTypeContext) {
        return getCompletionsForIfFunctionType(firstSibling, fields, start, currentContextId)
      }

      // For IFS context, return the default completions
      return getDefaultCompletions(fields, start, currentContextId)
    }
  }

  // If cannot determine the context, return empty array
  return []
}

/**
 * Returns the autocomplete options that are appropriate for the function type context
 */
function getCompletionsForFunctionType(
  context: FunctionCallTypeContext,
  fields: FormulaFieldReference[],
  start: number,
  currentContextId: string | undefined,
): Completion[] {
  if (context instanceof SumFunctionContext) {
    // For SUM, the fields must be of numeric type and in a different context
    const sumFields = filterFields(fields, {
      allowedTypes: [DataType.NUMBER, DataType.FORMULA_NUMBER, DataType.FORMULA],
      excludedContextIds: currentContextId ? [currentContextId] : undefined,
    })
    return sumFields.map((field) => getFieldAutocompletionOption(field, start, currentContextId))
  }
  if (context instanceof CountFunctionContext || context instanceof CountDistinctFunctionContext) {
    // For COUNT/COUNT_DISTINCT, the fields must be in a different context
    const countFields = filterFields(fields, {
      excludedContextIds: currentContextId ? [currentContextId] : undefined,
    })
    return countFields.map((field) => getFieldAutocompletionOption(field, start, currentContextId))
  }
  if (context instanceof AverageFunctionContext) {
    // AVERAGE is row-level as well as column-level, so — like MIN/MAX — it offers numeric fields
    // from the current context too, for AVERAGE({W}, {X}) over one row
    const averageFields = filterFields(fields, {
      allowedTypes: [DataType.NUMBER, DataType.FORMULA_NUMBER, DataType.FORMULA],
    })
    return averageFields.map((field) =>
      getFieldAutocompletionOption(field, start, currentContextId),
    )
  }
  if (context instanceof SumProductFunctionContext) {
    // SUMPRODUCT is column-only, so its operands come from another table
    const sumProductFields = filterFields(fields, {
      allowedTypes: [DataType.NUMBER, DataType.FORMULA_NUMBER, DataType.FORMULA],
      excludedContextIds: currentContextId ? [currentContextId] : undefined,
    })
    return sumProductFields.map((field) =>
      getFieldAutocompletionOption(field, start, currentContextId),
    )
  }
  if (context instanceof MaxFunctionContext || context instanceof MinFunctionContext) {
    // MIN/MAX order numbers or dates (earliest/latest), so offer both operand families
    const minOrMaxFields = filterFields(fields, {
      allowedTypes: [
        DataType.NUMBER,
        DataType.FORMULA_NUMBER,
        DataType.FORMULA,
        DataType.DATE,
        DataType.FORMULA_DATE,
        DataType.DATE_TIME,
        DataType.FORMULA_DATE_TIME,
      ],
    })
    return minOrMaxFields.map((field) =>
      getFieldAutocompletionOption(field, start, currentContextId),
    )
  }
  if (
    context instanceof CeilFunctionContext ||
    context instanceof FloorFunctionContext ||
    context instanceof RoundFunctionContext
  ) {
    // For CEIL/FLOOR/ROUND, allow the numeric fields on the current context and any function
    return getDefaultCompletions(fields, start, currentContextId)
  }
  if (context instanceof ToTextFunctionContext) {
    // TO_TEXT accepts every scalar type
    const toTextFields = filterFields(fields, {
      allowedTypes: [
        DataType.NUMBER,
        DataType.FORMULA_NUMBER,
        DataType.DATE,
        DataType.FORMULA_DATE,
        DataType.DATE_TIME,
        DataType.FORMULA_DATE_TIME,
        DataType.FORMULA,
        DataType.STRING,
        DataType.FORMULA_STRING,
        DataType.ENUM,
        DataType.FILE_URL,
        DataType.USER,
        DataType.ID,
      ],
    })
    // Include the function list so nested functions (e.g. TO_TEXT(YEAR(...))) can be autocompleted.
    return [
      ...FUNCTION_TYPE_COMPLETIONS,
      ...toTextFields.map((field) => getFieldAutocompletionOption(field, start, currentContextId)),
    ]
  }
  if (context instanceof ToDateFunctionContext) {
    // TO_DATE parses a text operand into a date, ortakes a date column as a no-op.
    const toDateFields = filterFields(fields, {
      allowedTypes: [
        DataType.STRING,
        DataType.FORMULA_STRING,
        DataType.ENUM,
        DataType.FILE_URL,
        DataType.USER,
        DataType.ID,
        DataType.DATE,
        DataType.FORMULA_DATE,
      ],
    })
    return [
      ...FUNCTION_TYPE_COMPLETIONS,
      ...toDateFields.map((field) => getFieldAutocompletionOption(field, start, currentContextId)),
    ]
  }
  if (isTextFunctionContext(context)) {
    // String functions operate on text operands, and several (CONCAT, LEFT/RIGHT/MID, TO_NUMBER)
    // legitimately mix in numeric arguments.
    // Allow the common operand types plus string-/number-producing formula columns.
    const stringFields = filterFields(fields, {
      allowedTypes: [
        DataType.STRING,
        DataType.FORMULA_STRING,
        DataType.NUMBER,
        DataType.FORMULA_NUMBER,
        DataType.FORMULA,
        DataType.FILE_URL,
        DataType.USER,
        DataType.ID,
        DataType.ENUM,
      ],
    })
    // Include the function list so nested functions (e.g. CONCAT(UPPER(...))) can be autocompleted
    // alongside the operand columns.
    return [
      ...FUNCTION_TYPE_COMPLETIONS,
      ...stringFields.map((field) => getFieldAutocompletionOption(field, start, currentContextId)),
    ]
  }
  if (isDateFunctionContext(context)) {
    // Date functions operate on date/datetime operands, with numeric args for DATEADD/DATEDIFF
    // Allow the common operand types across these functions plus date-producing formula columns.
    const dateFields = filterFields(fields, {
      allowedTypes: [
        DataType.DATE,
        DataType.FORMULA_DATE,
        DataType.DATE_TIME,
        DataType.FORMULA_DATE_TIME,
        DataType.NUMBER,
        DataType.FORMULA_NUMBER,
        DataType.FORMULA,
      ],
    })
    // Include the function list so nested functions (e.g. DATEADD(DATE(...))) can be autocompleted
    // alongside the operand columns.
    return [
      ...FUNCTION_TYPE_COMPLETIONS,
      ...dateFields.map((field) => getFieldAutocompletionOption(field, start, currentContextId)),
    ]
  }
  return []
}

/**
 * Returns the autocomplete options that are appropriate for the if function type context
 */
function getCompletionsForIfFunctionType(
  context: IfFunctionTypeContext,
  fields: FormulaFieldReference[],
  start: number,
  currentContextId: string | undefined,
): Completion[] {
  // Get the parent's child that is a two expression context to determine if the user
  // is in the logic expression portion of the context or the sum expression portion
  const parentContext = context.parentCtx

  const twoExpressionsContext = parentContext?.children?.find(
    (child): child is TwoExpressionsContext => child instanceof TwoExpressionsContext,
  )

  if (twoExpressionsContext) {
    // Determine which context is closest to the given position
    const closestContext = getClosestExpressionContext(twoExpressionsContext, start)

    if (closestContext instanceof LogicExprContext) {
      // Return all fields for logic expression context
      return fields.map((field) => getFieldAutocompletionOption(field, start, currentContextId))
    }

    if (closestContext instanceof SumExpressionContext) {
      if (context instanceof SumIfFunctionContext || context instanceof AverageIfFunctionContext) {
        // For SUM/AVERAGE, the fields must be of numeric type and in a different context
        const sumFields = filterFields(fields, {
          allowedTypes: [DataType.NUMBER, DataType.FORMULA_NUMBER, DataType.FORMULA],
          excludedContextIds: currentContextId ? [currentContextId] : undefined,
        })
        return sumFields.map((field) =>
          getFieldAutocompletionOption(field, start, currentContextId),
        )
      }
      if (context instanceof CountIfFunctionContext) {
        // For COUNT, the fields must be in a different context
        const countFields = filterFields(fields, {
          excludedContextIds: currentContextId ? [currentContextId] : undefined,
        })
        return countFields.map((field) =>
          getFieldAutocompletionOption(field, start, currentContextId),
        )
      }
      if (context instanceof MinIfsFunctionContext || context instanceof MaxIfsFunctionContext) {
        // MIN/MAX aggregate numbers or dates (earliest/latest), in a different context
        const minMaxFields = filterFields(fields, {
          allowedTypes: [
            DataType.NUMBER,
            DataType.FORMULA_NUMBER,
            DataType.FORMULA,
            DataType.DATE,
            DataType.FORMULA_DATE,
            DataType.DATE_TIME,
            DataType.FORMULA_DATE_TIME,
          ],
          excludedContextIds: currentContextId ? [currentContextId] : undefined,
        })
        return minMaxFields.map((field) =>
          getFieldAutocompletionOption(field, start, currentContextId),
        )
      }
    }
  }

  // If cannot determine the specific if function type or cursor's current context
  //  return an empty array
  return []
}

/**
 * Gets the decoration for the type of the token
 */
export function getDecorationForToken(token: CommonToken): Decoration {
  const tagType = TagTypeMapping[token.type] || 'default'
  const mark = markMap[tagType]
  return mark
}

const TagTypeMapping: { [key: number]: string } = {
  [formulaLexer.LEFT_PAREN]: 'parenthesis', // '('
  [formulaLexer.RIGHT_PAREN]: 'parenthesis', // ')'
  [formulaLexer.COMMA]: 'separator', // ','
  [formulaLexer.IFS]: 'function', // 'IFS'
  [formulaLexer.MAX]: 'function', // 'MAX'
  [formulaLexer.MIN]: 'function', // 'MIN'
  [formulaLexer.SUM]: 'function', // 'SUM'
  [formulaLexer.COUNT]: 'function', // 'COUNT'
  [formulaLexer.COUNT_DISTINCT]: 'function', // 'COUNT_DISTINCT'
  [formulaLexer.AVERAGE]: 'function', // 'AVERAGE'
  [formulaLexer.SUMPRODUCT]: 'function', // 'SUMPRODUCT'
  [formulaLexer.CEIL]: 'function', // 'CEIL'
  [formulaLexer.FLOOR]: 'function', // 'FLOOR'
  [formulaLexer.ROUND]: 'function', // 'ROUND'
  [formulaLexer.SUMIF]: 'function', // 'SUMIF'
  [formulaLexer.COUNTIF]: 'function', // 'COUNTIF'
  [formulaLexer.AVERAGEIF]: 'function', // 'AVERAGEIF'
  [formulaLexer.MINIFS]: 'function', // 'MINIFS'
  [formulaLexer.MAXIFS]: 'function', // 'MAXIFS'
  [formulaLexer.GT]: 'comparison-operator', // '>'
  [formulaLexer.GTE]: 'comparison-operator', // '>='
  [formulaLexer.LT]: 'comparison-operator', // '<'
  [formulaLexer.LTE]: 'comparison-operator', // '<='
  [formulaLexer.EQ]: 'comparison-operator', // '='
  [formulaLexer.NOT_EQ]: 'comparison-operator', // '!='
  [formulaLexer.NOT]: 'comparison-operator', // Not
  [formulaLexer.NOT_OP]: 'comparison-operator', // !
  [formulaLexer.AND]: 'comparison-operator', // And
  [formulaLexer.AND_OP]: 'comparison-operator', // &&
  [formulaLexer.OR]: 'comparison-operator', // Or
  [formulaLexer.OR_OP]: 'comparison-operator', // ||
  [formulaLexer.CONTAINS]: 'comparison-operator',
  [formulaLexer.DOES_NOT_CONTAIN]: 'comparison-operator',
  [formulaLexer.CONTAINS_ALL]: 'comparison-operator',
  [formulaLexer.CONTAINS_ANY]: 'comparison-operator',
  [formulaLexer.DOES_NOT_CONTAIN_ANY]: 'comparison-operator',
  [formulaLexer.IN]: 'comparison-operator',
  [formulaLexer.NOT_IN]: 'comparison-operator',
  [formulaLexer.IS_EMPTY]: 'comparison-operator',
  [formulaLexer.IS_NOT_EMPTY]: 'comparison-operator',
  [formulaLexer.COLUMN_ID]: 'variable',
  [formulaLexer.NUMBER]: 'number',
  [formulaLexer.STRING]: 'string',
  [formulaLexer.BOOLEAN]: 'boolean',
  [formulaLexer.PLUS]: 'operator', // '+'
  [formulaLexer.MINUS]: 'operator', // '-'
  [formulaLexer.MULTIPLY]: 'operator', // '*'
  [formulaLexer.DIVIDE]: 'operator', // '/'
}

const markMap: { [key: string]: Decoration } = {
  number: Decoration.mark({ class: 'cm-number', inclusiveEnd: true }),
  string: Decoration.mark({ class: 'cm-string', inclusiveEnd: true }),
  variable: Decoration.mark({ class: 'cm-variable', inclusiveEnd: true }),
  operator: Decoration.mark({ class: 'cm-operator', inclusive: true }),
  parenthesis: Decoration.mark({ class: 'cm-parenthesis', inclusiveEnd: true }),
  separator: Decoration.mark({ class: 'cm-separator', inclusiveEnd: true }),
  function: Decoration.mark({ class: 'cm-function', inclusiveEnd: true }),
  'comparison-operator': Decoration.mark({ class: 'cm-comparison-operator', inclusiveEnd: true }),
  boolean: Decoration.mark({ class: 'cm-boolean', inclusiveEnd: true }),
  default: Decoration.mark({ class: 'cm-default', inclusiveEnd: true }),
}
